import React from "react";
import type { Theme } from "./theme";

/**
 * Anivaze v4 — shared ANNOTATION primitives (REFERENCE-STUDY Rule 3: "point at
 * it"). Every winning reference names parts with leader-lines, connects
 * cause→effect with arrows, and emphasises with underlines / circles / brackets.
 * These were duplicated inside actors; this is the one theme-aware, deterministic
 * (pure function of `t`) home. Used by every actor + the Teacher/Whiteboard packs.
 *
 * Reveal trick: SVG `pathLength={1}` normalises any path to length 1, so
 * `strokeDasharray={1}` + `strokeDashoffset={1 - draw}` draws `draw∈[0,1]` of it
 * — no getTotalLength(), works in SSR/export, fully deterministic.
 */

const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);

/** target (tx,ty) → elbow/text (lx,ly) leader-line label. */
export const Label: React.FC<{
  tx: number; ty: number; lx: number; ly: number; text: string;
  theme: Theme; u: number; anchor?: "start" | "middle" | "end"; on?: number; color?: string;
}> = ({ tx, ty, lx, ly, text, theme, u, anchor = "start", on = 1, color }) => {
  const stroke = color ?? theme.palette.sub;
  const ink = color ?? theme.palette.ink;
  return (
    <g opacity={on} style={{ transition: "opacity .3s" }}>
      <line x1={tx} y1={ty} x2={lx} y2={ly} stroke={stroke} strokeWidth={Math.max(1, u * 0.12)} />
      <circle cx={tx} cy={ty} r={u * 0.25} fill={stroke} />
      <text x={anchor === "end" ? lx - u * 0.4 : lx + u * 0.4} y={ly + u * 0.5} fill={ink}
        fontSize={u * 1.6} fontWeight={700} textAnchor={anchor} fontFamily={theme.body.family}>{text}</text>
    </g>
  );
};

/** directional arrow (cause→effect). Curved if `bow`≠0. Draws in with `draw`. */
export const Arrow: React.FC<{
  x1: number; y1: number; x2: number; y2: number;
  theme: Theme; u: number; draw?: number; bow?: number; color?: string;
}> = ({ x1, y1, x2, y2, theme, u, draw = 1, bow = 0, color }) => {
  const stroke = color ?? theme.palette.primary;
  const w = Math.max(1.2, u * 0.28);
  const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
  const dx = x2 - x1, dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  // control point offset perpendicular to the line by `bow`
  const cxp = mx - (dy / len) * bow, cyp = my + (dx / len) * bow;
  const d = `M ${x1} ${y1} Q ${cxp} ${cyp} ${x2} ${y2}`;
  // arrowhead direction = from control point to end
  const adx = x2 - cxp, ady = y2 - cyp;
  const ang = Math.atan2(ady, adx);
  const hl = u * 1.4;
  const show = clamp01(draw) > 0.98;
  return (
    <g>
      <path d={d} fill="none" stroke={stroke} strokeWidth={w} strokeLinecap="round"
        pathLength={1} strokeDasharray={1} strokeDashoffset={1 - clamp01(draw)} />
      {show && (
        <path d={`M ${x2} ${y2} L ${x2 - hl * Math.cos(ang - 0.4)} ${y2 - hl * Math.sin(ang - 0.4)}
                  L ${x2 - hl * Math.cos(ang + 0.4)} ${y2 - hl * Math.sin(ang + 0.4)} Z`} fill={stroke} />
      )}
    </g>
  );
};

/** animated underline reveal under a phrase (x0..x1 at y). */
export const Underline: React.FC<{
  x0: number; x1: number; y: number; theme: Theme; u: number; draw?: number; color?: string;
}> = ({ x0, x1, y, theme, u, draw = 1, color }) => (
  <line x1={x0} y1={y} x2={x1} y2={y} stroke={color ?? theme.palette.highlight}
    strokeWidth={Math.max(2, u * 0.4)} strokeLinecap="round"
    pathLength={1} strokeDasharray={1} strokeDashoffset={1 - clamp01(draw)} />
);

/** the "hand-drawn circle around it": ellipse stroke that draws in with `draw`. */
export const CircleMark: React.FC<{
  cx: number; cy: number; rx: number; ry: number; theme: Theme; u: number; draw?: number; color?: string;
}> = ({ cx, cy, rx, ry, theme, u, draw = 1, color }) => (
  <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="none" stroke={color ?? theme.palette.danger}
    strokeWidth={Math.max(2, u * 0.35)} strokeLinecap="round"
    pathLength={1} strokeDasharray={1} strokeDashoffset={1 - clamp01(draw)}
    transform={`rotate(-6 ${cx} ${cy})`} />
);

/** brace grouping a vertical (default) or horizontal region, with a label. */
export const Bracket: React.FC<{
  a: number; b: number; at: number; label?: string; theme: Theme; u: number;
  orient?: "v" | "h"; color?: string;
}> = ({ a, b, at, label, theme, u, orient = "v", color }) => {
  const stroke = color ?? theme.palette.sub;
  const k = u * 1.2; // brace depth
  const mid = (a + b) / 2;
  const d = orient === "v"
    ? `M ${at} ${a} q ${-k} 0 ${-k} ${k} L ${at - k} ${mid - k} q 0 ${k} ${-k} ${k} q ${k} 0 ${k} ${k} L ${at - k} ${b - k} q 0 ${k} ${k} ${k}`
    : `M ${a} ${at} q 0 ${-k} ${k} ${-k} L ${mid - k} ${at - k} q ${k} 0 ${k} ${-k} q 0 ${k} ${k} ${k} L ${b - k} ${at - k} q ${k} 0 ${k} ${k}`;
  return (
    <g>
      <path d={d} fill="none" stroke={stroke} strokeWidth={Math.max(1.2, u * 0.22)} strokeLinecap="round" />
      {label && (
        <text x={orient === "v" ? at - k * 2 : mid} y={orient === "v" ? mid : at - k * 2}
          fill={theme.palette.ink} fontSize={u * 1.5} fontWeight={700}
          textAnchor={orient === "v" ? "end" : "middle"} fontFamily={theme.body.family}>{label}</text>
      )}
    </g>
  );
};

/**
 * Handwrite — reveal any stroked path(s) as if written by hand. Pass an SVG path
 * `d` (or `children` that are stroked paths carrying pathLength={1}); `draw`∈[0,1]
 * controls how much is written. The Teacher/Whiteboard "the pen writes it" effect.
 */
export const Handwrite: React.FC<{
  d?: string; draw?: number; theme: Theme; u?: number; color?: string; children?: React.ReactNode;
}> = ({ d, draw = 1, theme, u = 10, color, children }) => {
  const off = 1 - clamp01(draw);
  if (d) {
    return (
      <path d={d} fill="none" stroke={color ?? theme.palette.ink}
        strokeWidth={Math.max(1.5, u * 0.3)} strokeLinecap="round" strokeLinejoin="round"
        pathLength={1} strokeDasharray={1} strokeDashoffset={off} />
    );
  }
  // wrap children: apply the dash reveal to each stroked child via a group
  return <g style={{ strokeDasharray: 1, strokeDashoffset: off }}>{children}</g>;
};
